import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faExclamationTriangle, faCog, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import ScanQueue from './ScanQueue';
import ScanResults from './ScanResults';
import ScanConfig from './ScanConfig';
import webVulnScanService from '../../services/webVulnScanService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import './Scanner.css';

const Scanner = ({ webTargetId, projectId }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [activeTab, setActiveTab] = useState('queue');
  const [loading, setLoading] = useState(false);
  const selectedScanRef = useRef(selectedScan);
  selectedScanRef.current = selectedScan;

  const loadScans = async () => {
    try {
      const data = await webVulnScanService.listScans(webTargetId);

      const scansArray = Array.isArray(data) ? data : [];

      scansArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setScans(scansArray);

      const current = selectedScanRef.current;
      if (current) {
        const fresh = scansArray.find(s => s._id === current._id);
        if (fresh) setSelectedScan(fresh);
      } else if (scansArray.length > 0) {
        setSelectedScan(scansArray[0]);
      }
    } catch (error) {
      console.error('Failed to load scans:', error);
      setScans([]);
    }
  };

  useEffect(() => {
    if (webTargetId) {
      loadScans();
      const interval = setInterval(loadScans, 3000);
      return () => clearInterval(interval);
    }
  }, [webTargetId]);

  const handlePause = async (scanId) => {
    try {
      await webVulnScanService.pauseScan(scanId);
      loadScans();
    } catch (error) {
      console.error('Failed to pause scan:', error);
    }
  };

  const handleResume = async (scanId) => {
    try {
      await webVulnScanService.resumeScan(scanId);
      loadScans();
    } catch (error) {
      console.error('Failed to resume scan:', error);
    }
  };

  const handleCancel = async (scanId) => {
    try {
      await webVulnScanService.cancelScan(scanId);
      loadScans();
    } catch (error) {
      console.error('Failed to cancel scan:', error);
    }
  };

  const handleDelete = async (scanId) => {
    if (!window.confirm('Delete this scan? This cannot be undone.')) return;

    try {
      await webVulnScanService.deleteScan(scanId);
      if (selectedScan?._id === scanId) {
        setSelectedScan(null);
      }
      loadScans();
      // Show success notification (add toast import if not already there)
      console.log('Scan deleted successfully');
    } catch (error) {
      console.error('Failed to delete scan:', error);
      alert('Failed to delete scan: ' + error.message);
    }
  };

  // Dark mode styles - only override what's necessary, let CSS handle sizing
  const styles = {
    page: {
      background: darkMode ? '#111827' : '#f3f4f6',
      minHeight: '100vh'
    },
    tabs: {
      background: darkMode ? '#1f2937' : '#ffffff',
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
    },
    content: {
      background: darkMode ? '#111827' : '#f3f4f6'
    }
  };

  return (
    <div className="scanner-page" style={styles.page}>
      <div className="scanner-tabs" style={styles.tabs}>
        <button
          className={`tab-button ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
        >
          <FontAwesomeIcon icon={faList} />
          <span>{t('webTesting.scanQueue')}</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
          disabled={!selectedScan}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{t('webTesting.scanResults')}</span>
          {selectedScan?.progress?.vulnerabilitiesFound > 0 && (
            <span className="badge">{selectedScan.progress.vulnerabilitiesFound}</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <FontAwesomeIcon icon={faCog} />
          <span>{t('webTesting.newScan')}</span>
        </button>

        {/* Refresh button - only show in queue/results tabs */}
        {(activeTab === 'queue' || activeTab === 'results') && (
          <button
            onClick={loadScans}
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              background: 'transparent',
              border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
              borderRadius: '6px',
              color: darkMode ? '#9ca3af' : '#6b7280',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? '#374151' : '#f3f4f6';
              e.currentTarget.style.color = darkMode ? '#60a5fa' : '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = darkMode ? '#9ca3af' : '#6b7280';
            }}
            title={t('common.refresh')}
          >
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>
        )}
      </div>

      <div className="scanner-content" style={styles.content}>
        {activeTab === 'queue' && (
          <ScanQueue
            scans={scans}
            selectedScan={selectedScan}
            onSelect={setSelectedScan}
            onPause={handlePause}
            onResume={handleResume}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onRefresh={loadScans}
            onViewResults={(scan) => { setSelectedScan(scan); setActiveTab('results'); }}
          />
        )}

        {activeTab === 'results' && selectedScan && (
          <ScanResults
            scan={selectedScan}
            onRefresh={loadScans}
          />
        )}

        {activeTab === 'config' && (
          <ScanConfig
            webTargetId={webTargetId}
            projectId={projectId}
            onScanCreated={loadScans}
          />
        )}
      </div>
    </div>
  );
};

export default Scanner;
